import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Mail } from "lucide-react";
import { SITE, NAV_LINKS } from "@/constants/site";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 bg-white border-b transition-shadow ${
        scrolled ? "shadow-sm border-slate-200" : "border-transparent"
      }`}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 bg-[#1B3A6B] text-white px-3 py-2 rounded"
      >
        Skip to content
      </a>
      <div className="container-narrow flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3"
          aria-label="Kernected Disability home"
        >
          <img
            src={SITE.logoUrl}
            alt="Kernected Disability Support Services logo"
            className="h-12 w-auto object-contain"
          />
          <span className="sr-only">{SITE.name}</span>
        </Link>

        <nav
          aria-label="Primary"
          className="hidden md:flex items-center gap-8"
        >
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[15px] font-medium transition-colors ${
                  isActive
                    ? "text-[#1B3A6B]"
                    : "text-slate-700 hover:text-[#1B3A6B]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-enquire"
            className="inline-flex items-center gap-2 bg-[#1B3A6B] text-white hover:bg-[#132A4D] px-5 py-2.5 rounded-lg font-medium transition-colors"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            Enquire now
          </Link>
        </nav>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg border border-slate-200 text-slate-700 hover:text-[#1B3A6B] hover:border-[#1B3A6B]"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden border-t border-slate-200 bg-white"
        >
          <nav className="container-narrow flex flex-col py-4" aria-label="Mobile">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium ${
                    isActive ? "text-[#1B3A6B]" : "text-slate-800"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta-enquire"
              className="mt-3 inline-flex justify-center items-center gap-2 bg-[#1B3A6B] text-white hover:bg-[#132A4D] px-5 py-3 rounded-lg font-medium"
            >
              <Mail className="h-4 w-4" /> Enquire now
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
